import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  Loader2,
  CalendarOff,
  UserCheck,
  RefreshCw,
  ArrowLeftRight,
  List,
  CalendarDays,
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, startOfDay, isBefore } from 'date-fns';
import { timeOffApi, availabilityApi, coverageApi, shiftSwapsApi } from '../services/api';

// ===========================================
// TYPES
// ===========================================

interface TimeOffRequest {
  id: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  reason?: string;
  status: 'pending' | 'approved' | 'denied' | 'cancelled';
  reviewerNotes?: string;
  createdAt: string;
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  };
}

interface AvailabilityWindow {
  id: string;
  employeeId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isPreferred: boolean;
  status: 'pending' | 'active' | 'rejected';
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  };
}

interface CoverageGap {
  shiftInstanceId: string;
  shiftName: string;
  date: string;
  startTime: string;
  endTime: string;
  role: string;
  currentAssigned: number;
  required: number;
  gap: number;
}

interface ShiftSwapRequest {
  id: string;
  originalAssignmentId: string;
  requestingEmployeeId: string;
  targetEmployeeId?: string;
  swapType: 'give_away' | 'swap' | 'trade';
  status: 'pending' | 'accepted' | 'manager_pending' | 'approved' | 'denied' | 'cancelled' | 'expired';
  reason?: string;
  createdAt: string;
  originalAssignment?: {
    id: string;
    role: string;
    scheduledStart: string;
    scheduledEnd: string;
    shiftInstance?: {
      id: string;
      name: string;
      date: string;
    };
  };
  requestingEmployee?: {
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  };
  targetEmployee?: {
    id: string;
    firstName: string;
    lastName: string;
    primaryRole: string;
  };
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatDateRange = (start: string, end: string) => {
  const startDate = parseISO(start);
  const endDate = parseISO(end);

  if (start === end) {
    return format(startDate, 'MMM d, yyyy');
  }

  if (startDate.getFullYear() === endDate.getFullYear()) {
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`;
    }
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
  }

  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
};

const getDateLabel = (dateStr: string) => {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'EEE, MMM d');
};

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function LaborPage() {
  const [activeTab, setActiveTab] = useState<'time-off' | 'availability' | 'coverage' | 'swaps'>('time-off');
  const queryClient = useQueryClient();

  // Fetch time-off requests
  const { data: timeOffData, isLoading: loadingTimeOff } = useQuery({
    queryKey: ['time-off-requests'],
    queryFn: async () => {
      const response = await timeOffApi.getAll();
      return response.data;
    },
  });

  // Fetch availability windows
  const { data: availabilityData, isLoading: loadingAvailability } = useQuery({
    queryKey: ['availability-windows'],
    queryFn: async () => {
      const response = await availabilityApi.getAll();
      return response.data;
    },
  });

  // Fetch coverage gaps
  const { data: coverageData, isLoading: loadingCoverage } = useQuery({
    queryKey: ['coverage-gaps'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = addDays(new Date(), 7).toISOString().split('T')[0];
      const response = await coverageApi.getGaps(today, nextWeek);
      return response.data;
    },
  });

  // Fetch pending shift swaps
  const { data: swapsData, isLoading: loadingSwaps } = useQuery({
    queryKey: ['pending-shift-swaps'],
    queryFn: async () => {
      const response = await shiftSwapsApi.getPending();
      return response.data;
    },
  });

  // Count pending items for badges
  const pendingTimeOff = timeOffData?.data?.filter((r: TimeOffRequest) => r.status === 'pending')?.length || 0;
  const pendingAvailability = availabilityData?.data?.filter((w: AvailabilityWindow) => w.status === 'pending')?.length || 0;
  const totalGaps = coverageData?.data?.summary?.totalGaps || 0;
  const pendingSwaps = swapsData?.count || 0;

  const tabs = [
    { id: 'time-off' as const, label: 'Time-Off', icon: CalendarOff, badge: pendingTimeOff },
    { id: 'availability' as const, label: 'Availability', icon: UserCheck, badge: pendingAvailability },
    { id: 'coverage' as const, label: 'Coverage Gaps', icon: AlertTriangle, badge: totalGaps },
    { id: 'swaps' as const, label: 'Shift Swaps', icon: ArrowLeftRight, badge: pendingSwaps },
  ];

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 truncate">Labor Dashboard</h1>
            <p className="text-sm text-slate-500 truncate">Manage time-off, availability, and coverage</p>
          </div>
        </div>
        <button
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['time-off-requests'] });
            queryClient.invalidateQueries({ queryKey: ['availability-windows'] });
            queryClient.invalidateQueries({ queryKey: ['coverage-gaps'] });
            queryClient.invalidateQueries({ queryKey: ['pending-shift-swaps'] });
          }}
          className="btn btn-secondary flex items-center gap-2 self-start sm:self-auto shrink-0"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs — horizontally scrollable on narrow screens */}
      <div className="border-b border-slate-200 mb-6 overflow-x-auto">
        <nav className="flex gap-4 sm:gap-6 whitespace-nowrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors shrink-0 ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  tab.id === 'coverage'
                    ? 'bg-warning-100 text-warning-800'
                    : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'time-off' && (
        <TimeOffTab
          requests={timeOffData?.data || []}
          isLoading={loadingTimeOff}
        />
      )}
      {activeTab === 'availability' && (
        <AvailabilityTab
          windows={availabilityData?.data || []}
          isLoading={loadingAvailability}
        />
      )}
      {activeTab === 'coverage' && (
        <CoverageTab
          gaps={coverageData?.data?.gaps || []}
          summary={coverageData?.data?.summary}
          isLoading={loadingCoverage}
        />
      )}
      {activeTab === 'swaps' && (
        <ShiftSwapsTab
          requests={swapsData?.data || []}
          isLoading={loadingSwaps}
        />
      )}
    </div>
  );
}

// ===========================================
// TIME-OFF TAB
// ===========================================

function TimeOffTab({
  requests,
  isLoading,
}: {
  requests: TimeOffRequest[];
  isLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<TimeOffRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const approveMutation = useMutation({
    mutationFn: (id: string) => timeOffApi.approve(id, reviewNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-off-requests'] });
      setSelectedRequest(null);
      setReviewNotes('');
    },
  });

  const denyMutation = useMutation({
    mutationFn: (id: string) => timeOffApi.deny(id, reviewNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-off-requests'] });
      setSelectedRequest(null);
      setReviewNotes('');
    },
  });

  const pendingRequests = requests.filter((r) => r.status === 'pending');
  const resolvedRequests = requests.filter((r) => r.status !== 'pending');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-600" />
          Pending Requests ({pendingRequests.length})
        </h2>

        {pendingRequests.length === 0 ? (
          <div className="card p-8 text-center">
            <CheckCircle className="w-12 h-12 text-success-500 mx-auto mb-3" />
            <p className="text-slate-500">No pending time-off requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingRequests.map((request) => (
              <div
                key={request.id}
                className="card p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedRequest(request)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-indigo-700 font-medium">
                        {request.employee?.firstName?.[0]}{request.employee?.lastName?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {request.employee?.firstName} {request.employee?.lastName}
                      </p>
                      <p className="text-sm text-slate-500">
                        {request.employee?.primaryRole}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-slate-900">
                      {formatDateRange(request.startDate, request.endDate)}
                    </p>
                    <p className="text-sm text-slate-500">
                      {request.reason || 'No reason provided'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Resolved */}
      {resolvedRequests.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Decisions</h2>
          <div className="space-y-2">
            {resolvedRequests.slice(0, 5).map((request) => (
              <div
                key={request.id}
                className="card p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className={`badge ${
                    request.status === 'approved' ? 'badge-success' : 'badge-danger'
                  }`}>
                    {request.status}
                  </span>
                  <span className="text-slate-700">
                    {request.employee?.firstName} {request.employee?.lastName}
                  </span>
                </div>
                <span className="text-sm text-slate-500">
                  {formatDateRange(request.startDate, request.endDate)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Review Time-Off Request</h2>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-sm text-slate-500">Employee</p>
                <p className="font-medium">
                  {selectedRequest.employee?.firstName} {selectedRequest.employee?.lastName}
                  <span className="text-slate-500 ml-2">({selectedRequest.employee?.primaryRole})</span>
                </p>
              </div>

              <div>
                <p className="text-sm text-slate-500">Dates</p>
                <p className="font-medium">
                  {formatDateRange(selectedRequest.startDate, selectedRequest.endDate)}
                </p>
              </div>

              {selectedRequest.reason && (
                <div>
                  <p className="text-sm text-slate-500">Reason</p>
                  <p className="text-slate-700">{selectedRequest.reason}</p>
                </div>
              )}

              <div>
                <label className="text-sm text-slate-500 block mb-1">Notes (optional)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Add a note for the employee..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setReviewNotes('');
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => denyMutation.mutate(selectedRequest.id)}
                disabled={denyMutation.isPending}
                className="flex-1 btn btn-danger flex items-center justify-center gap-2"
              >
                {denyMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Deny
              </button>
              <button
                onClick={() => approveMutation.mutate(selectedRequest.id)}
                disabled={approveMutation.isPending}
                className="flex-1 btn btn-primary flex items-center justify-center gap-2"
              >
                {approveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================
// AVAILABILITY TAB
// ===========================================

function AvailabilityTab({
  windows,
  isLoading,
}: {
  windows: AvailabilityWindow[];
  isLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const approveMutation = useMutation({
    mutationFn: (windowId: string) => availabilityApi.approve(windowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-windows'] });
    },
  });

  const bulkApproveMutation = useMutation({
    mutationFn: () => availabilityApi.bulkReview(Array.from(selectedIds), 'approve'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-windows'] });
      setSelectedIds(new Set());
    },
  });

  const pendingWindows = windows.filter((w) => w.status === 'pending');

  // Group by employee
  const byEmployee = pendingWindows.reduce((acc, window) => {
    const empId = window.employeeId;
    if (!acc[empId]) {
      acc[empId] = {
        employee: window.employee,
        windows: [],
      };
    }
    acc[empId].windows.push(window);
    return acc;
  }, {} as Record<string, { employee: any; windows: AvailabilityWindow[] }>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAllForEmployee = (empId: string) => {
    const newSet = new Set(selectedIds);
    byEmployee[empId].windows.forEach((w) => newSet.add(w.id));
    setSelectedIds(newSet);
  };

  return (
    <div className="space-y-6">
      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="card p-4 bg-indigo-50 border-indigo-200 flex items-center justify-between">
          <span className="text-indigo-700 font-medium">
            {selectedIds.size} window{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="btn btn-secondary btn-sm"
            >
              Clear
            </button>
            <button
              onClick={() => bulkApproveMutation.mutate()}
              disabled={bulkApproveMutation.isPending}
              className="btn btn-primary btn-sm flex items-center gap-2"
            >
              {bulkApproveMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Approve Selected
            </button>
          </div>
        </div>
      )}

      {/* Pending by Employee */}
      {Object.keys(byEmployee).length === 0 ? (
        <div className="card p-8 text-center">
          <CheckCircle className="w-12 h-12 text-success-500 mx-auto mb-3" />
          <p className="text-slate-500">No pending availability submissions</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(byEmployee).map(([empId, { employee, windows: empWindows }]) => (
            <div key={empId} className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-indigo-700 font-medium">
                      {employee?.firstName?.[0]}{employee?.lastName?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {employee?.firstName} {employee?.lastName}
                    </p>
                    <p className="text-sm text-slate-500">{employee?.primaryRole}</p>
                  </div>
                </div>
                <button
                  onClick={() => selectAllForEmployee(empId)}
                  className="text-sm text-indigo-600 hover:underline"
                >
                  Select All ({empWindows.length})
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {empWindows.map((window) => (
                  <div
                    key={window.id}
                    onClick={() => toggleSelect(window.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedIds.has(window.id)
                        ? 'bg-indigo-50 border-indigo-300'
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-900">
                        {DAYS_OF_WEEK[window.dayOfWeek]}
                      </span>
                      {window.isPreferred && (
                        <span className="text-xs text-indigo-600">Preferred</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      {window.startTime} - {window.endTime}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    empWindows.forEach((w) => approveMutation.mutate(w.id));
                  }}
                  className="btn btn-primary btn-sm"
                >
                  Approve All for {employee?.firstName}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================
// COVERAGE TAB
// ===========================================

function CoverageTab({
  gaps,
  summary,
  isLoading,
}: {
  gaps: CoverageGap[];
  summary?: { totalGaps: number; shiftsAffected: number; byRole: Record<string, number> };
  isLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const [expandedGapKey, setExpandedGapKey] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [assigningTo, setAssigningTo] = useState<string | null>(null);
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));

  // Filter out past shifts - only show shifts that haven't started yet
  // Use startOfDay for consistent date comparison (avoid timezone issues)
  const now = new Date();
  const todayStart = startOfDay(now);
  const filteredGaps = gaps.filter(gap => {
    // Parse the gap date and compare using date-fns for consistency
    const gapDate = parseISO(gap.date);
    if (isBefore(gapDate, todayStart)) return false;
    // For today's shifts, also check if the shift has already started
    if (isSameDay(gapDate, now) && gap.startTime) {
      const startTime = new Date(gap.startTime);
      if (isBefore(startTime, now)) return false;
    }
    return true;
  });

  // Toggle date collapse
  const toggleDateCollapse = (date: string) => {
    setCollapsedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // Load candidates when a gap is expanded
  const loadCandidates = async (shiftInstanceId: string, role: string) => {
    const gapKey = `${shiftInstanceId}-${role}`;
    if (expandedGapKey === gapKey) {
      setExpandedGapKey(null);
      return;
    }

    setExpandedGapKey(gapKey);
    setLoadingCandidates(true);
    try {
      const response = await coverageApi.getGapCandidates(shiftInstanceId, role, 5);
      setCandidates(response.data?.data?.candidates || []);
    } catch (error) {
      console.error('Failed to load candidates:', error);
      setCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Assign mutation - fill the gap with a new assignment
  const handleAssign = async (shiftInstanceId: string, employeeId: string, role: string) => {
    setAssigningTo(employeeId);
    try {
      await coverageApi.fillGap(shiftInstanceId, employeeId, role);
      queryClient.invalidateQueries({ queryKey: ['coverage-gaps'] });
      setExpandedGapKey(null);
      setCandidates([]);
    } catch (error) {
      console.error('Failed to assign:', error);
    } finally {
      setAssigningTo(null);
    }
  };

  // Send coverage request to eligible employees
  const handleRequestCoverage = async (shiftInstanceId: string, role: string) => {
    try {
      // For gaps, we don't have an assignmentId, so we'll create one or use a different approach
      // For now, just show a success message - this could be enhanced to send notifications
      alert(`Coverage request sent for ${role} position`);
    } catch (error) {
      console.error('Failed to request coverage:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (filteredGaps.length === 0) {
    return (
      <div className="card p-8 text-center">
        <CheckCircle className="w-12 h-12 text-success-500 mx-auto mb-3" />
        <p className="text-lg font-medium text-slate-900">All Shifts Covered</p>
        <p className="text-slate-500">No coverage gaps in upcoming shifts</p>
      </div>
    );
  }

  // Group gaps by date
  const byDate = filteredGaps.reduce((acc, gap) => {
    if (!acc[gap.date]) acc[gap.date] = [];
    acc[gap.date].push(gap);
    return acc;
  }, {} as Record<string, CoverageGap[]>);

  // Recalculate summary for filtered gaps
  const filteredSummary = {
    totalGaps: filteredGaps.reduce((sum, g) => sum + g.gap, 0),
    shiftsAffected: filteredGaps.length,
    byRole: filteredGaps.reduce((acc, gap) => {
      if (!acc[gap.role]) acc[gap.role] = 0;
      acc[gap.role] += gap.gap;
      return acc;
    }, {} as Record<string, number>),
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-warning-700" />
            </div>
            <div>
              <p className="stat-value">{filteredSummary.totalGaps}</p>
              <p className="stat-label">Open Positions</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="stat-value">{filteredSummary.shiftsAffected}</p>
              <p className="stat-label">Shifts Affected</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="stat-value">{Object.keys(filteredSummary.byRole).length}</p>
              <p className="stat-label">Roles Affected</p>
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Coverage Gaps</h2>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 shrink-0">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'calendar'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            Calendar
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="card">
          {/* Calendar Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <button
              onClick={() => setCalendarWeekStart(addDays(calendarWeekStart, -7))}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h3 className="font-semibold text-slate-900">
              {format(calendarWeekStart, 'MMM d')} - {format(addDays(calendarWeekStart, 6), 'MMM d, yyyy')}
            </h3>
            <button
              onClick={() => setCalendarWeekStart(addDays(calendarWeekStart, 7))}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Calendar Grid — responsive: 2-up on phone, 4-up tablet, 7-up desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {eachDayOfInterval({
              start: calendarWeekStart,
              end: endOfWeek(calendarWeekStart, { weekStartsOn: 0 }),
            }).map((day) => {
              const dayStr = format(day, 'yyyy-MM-dd');
              const dayGaps = byDate[dayStr] || [];
              const totalGapsForDay = dayGaps.reduce((sum, g) => sum + g.gap, 0);
              const isPast = isBefore(day, todayStart);
              const isCurrentDay = isSameDay(day, now);

              return (
                <div
                  key={dayStr}
                  className={`min-h-[100px] lg:min-h-[140px] p-2 border border-slate-200 rounded-lg ${
                    isPast ? 'bg-slate-50 opacity-50' : ''
                  } ${isCurrentDay ? 'bg-indigo-50 border-indigo-200' : ''}`}
                >
                  <div className="text-center mb-2">
                    <p className="text-xs text-slate-500 uppercase">{format(day, 'EEE')}</p>
                    <p className={`text-lg font-semibold ${
                      isCurrentDay ? 'text-indigo-600' : 'text-slate-900'
                    }`}>
                      {format(day, 'd')}
                    </p>
                  </div>

                  {totalGapsForDay > 0 && !isPast ? (
                    <div className="space-y-1">
                      {dayGaps.slice(0, 3).map((gap, idx) => (
                        <div
                          key={`${gap.shiftInstanceId}-${gap.role}-${idx}`}
                          className="text-xs p-1.5 bg-warning-100 text-warning-800 rounded truncate"
                          title={`${gap.shiftName} - ${gap.role}: ${gap.gap} needed`}
                        >
                          <span className="font-medium">{gap.gap}</span> {gap.role}
                        </div>
                      ))}
                      {dayGaps.length > 3 && (
                        <p className="text-xs text-slate-500 text-center">
                          +{dayGaps.length - 3} more
                        </p>
                      )}
                    </div>
                  ) : !isPast ? (
                    <div className="text-xs text-slate-400 text-center mt-4">
                      <CheckCircle className="w-4 h-4 mx-auto text-success-400" />
                      <p className="mt-1">Covered</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View - Gaps by Date - Collapsible */}
      {viewMode === 'list' && (
      <div className="space-y-4">
        {Object.entries(byDate)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, dateGaps]) => {
            const isCollapsed = collapsedDates.has(date);
            return (
            <div key={date} className="card overflow-hidden">
              <button
                onClick={() => toggleDateCollapse(date)}
                className="w-full px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between hover:bg-slate-100 transition-colors"
              >
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  {getDateLabel(date)}
                  <span className="text-sm font-normal text-slate-500">
                    ({dateGaps.reduce((sum, g) => sum + g.gap, 0)} positions)
                  </span>
                </h3>
                {isCollapsed ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                )}
              </button>

              {!isCollapsed && (
              <div className="divide-y divide-slate-100">
                {dateGaps.map((gap) => {
                  const gapKey = `${gap.shiftInstanceId}-${gap.role}`;
                  const isExpanded = expandedGapKey === gapKey;

                  return (
                    <div key={gapKey}>
                      {/* Gap Row */}
                      <div className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                            gap.gap >= 3 ? 'bg-red-100 text-red-700' :
                            gap.gap >= 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            +{gap.gap}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{gap.shiftName}</p>
                            <p className="text-sm text-slate-500">
                              {format(parseISO(gap.startTime), 'h:mm a')} - {format(parseISO(gap.endTime), 'h:mm a')}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right mr-2">
                            <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">
                              {gap.role}
                            </span>
                            <p className="text-sm text-slate-500 mt-1">
                              {gap.currentAssigned}/{gap.required} assigned
                            </p>
                          </div>

                          {/* Action Buttons */}
                          <button
                            onClick={() => loadCandidates(gap.shiftInstanceId, gap.role)}
                            className={`btn btn-sm ${isExpanded ? 'btn-primary' : 'btn-secondary'}`}
                          >
                            {isExpanded ? 'Close' : 'Find Coverage'}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Candidates Panel */}
                      {isExpanded && (
                        <div className="px-4 pb-4 bg-indigo-50/30">
                          {loadingCandidates ? (
                            <div className="flex items-center justify-center py-6 text-slate-500">
                              <Loader2 className="w-5 h-5 animate-spin mr-2" />
                              Finding best candidates...
                            </div>
                          ) : candidates.length === 0 ? (
                            <div className="py-6 text-center">
                              <p className="text-slate-600 mb-3">No available candidates found</p>
                              <button
                                onClick={() => handleRequestCoverage(gap.shiftInstanceId, gap.role)}
                                className="btn btn-primary btn-sm"
                              >
                                Send Coverage Request to All Staff
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-slate-600 py-2">
                                Top candidates (ranked by availability & fit):
                              </p>
                              {candidates.map((candidate: any, index: number) => (
                                <div
                                  key={candidate.employeeId}
                                  className={`flex items-center justify-between p-3 rounded-lg border ${
                                    index === 0
                                      ? 'bg-emerald-50 border-emerald-200'
                                      : 'bg-white border-slate-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                      index === 0 ? 'bg-emerald-500 text-white' :
                                      index === 1 ? 'bg-slate-300 text-slate-700' :
                                      'bg-slate-200 text-slate-600'
                                    }`}>
                                      {index + 1}
                                    </div>
                                    <div>
                                      <p className="font-medium text-slate-900">
                                        {candidate.employeeName}
                                        {index === 0 && (
                                          <span className="ml-2 text-xs text-emerald-600 font-normal">
                                            ★ Best Match
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-sm text-slate-500">
                                        {candidate.primaryRole}
                                        {candidate.warnings?.length > 0 && (
                                          <span className="text-amber-600 ml-2">
                                            • {candidate.warnings[0]}
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-3">
                                    <div className="text-right text-sm">
                                      <span className="text-slate-600">
                                        Score: {Math.round(candidate.score)}%
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => handleAssign(gap.shiftInstanceId, candidate.employeeId, gap.role)}
                                      disabled={assigningTo === candidate.employeeId}
                                      className={`btn btn-sm ${
                                        index === 0 ? 'btn-primary' : 'btn-secondary'
                                      }`}
                                    >
                                      {assigningTo === candidate.employeeId ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        'Assign'
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ))}

                              <div className="pt-3 flex justify-end">
                                <button
                                  onClick={() => handleRequestCoverage(gap.shiftInstanceId, gap.role)}
                                  className="text-sm text-indigo-600 hover:underline"
                                >
                                  Notify all eligible employees →
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          );
          })}
      </div>
      )}
    </div>
  );
}

// ===========================================
// SHIFT SWAPS TAB
// ===========================================

function ShiftSwapsTab({
  requests,
  isLoading,
}: {
  requests: ShiftSwapRequest[];
  isLoading: boolean;
}) {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<ShiftSwapRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const approveMutation = useMutation({
    mutationFn: (id: string) => shiftSwapsApi.approve(id, reviewNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-shift-swaps'] });
      setSelectedRequest(null);
      setReviewNotes('');
    },
  });

  const denyMutation = useMutation({
    mutationFn: (id: string) => shiftSwapsApi.deny(id, reviewNotes || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-shift-swaps'] });
      setSelectedRequest(null);
      setReviewNotes('');
    },
  });

  const swapTypeLabels: Record<string, string> = {
    give_away: 'Give Away',
    swap: 'Swap',
    trade: 'Trade',
  };

  const formatShiftTime = (start: string, end: string) => {
    return `${format(parseISO(start), 'h:mm a')} - ${format(parseISO(end), 'h:mm a')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="card p-8 text-center">
        <CheckCircle className="w-12 h-12 text-success-500 mx-auto mb-3" />
        <p className="text-lg font-medium text-slate-900">No Pending Swaps</p>
        <p className="text-slate-500">All shift swap requests have been reviewed</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
          Pending Swap Requests ({requests.length})
        </h2>

        <div className="space-y-3">
          {requests.map((request) => (
            <div
              key={request.id}
              className="card p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedRequest(request)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-indigo-700 font-medium">
                      {request.requestingEmployee?.firstName?.[0]}{request.requestingEmployee?.lastName?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {request.requestingEmployee?.firstName} {request.requestingEmployee?.lastName}
                    </p>
                    <p className="text-sm text-slate-500">
                      wants to <span className="font-medium">{swapTypeLabels[request.swapType].toLowerCase()}</span> a shift
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {request.originalAssignment?.shiftInstance && (
                    <>
                      <p className="font-medium text-slate-900">
                        {format(parseISO(request.originalAssignment.shiftInstance.date), 'EEE, MMM d')}
                      </p>
                      <p className="text-sm text-slate-500">
                        {request.originalAssignment.shiftInstance.name}
                      </p>
                    </>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 ml-4" />
              </div>

              {request.targetEmployee && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">{request.targetEmployee.firstName} {request.targetEmployee.lastName}</span>
                    {' '}has accepted this swap
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Review Shift Swap</h2>

            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3">
                <span className={`badge ${
                  selectedRequest.swapType === 'give_away' ? 'badge-warning' : 'badge-primary'
                }`}>
                  {swapTypeLabels[selectedRequest.swapType]}
                </span>
              </div>

              <div>
                <p className="text-sm text-slate-500">Requesting Employee</p>
                <p className="font-medium">
                  {selectedRequest.requestingEmployee?.firstName} {selectedRequest.requestingEmployee?.lastName}
                  <span className="text-slate-500 ml-2">({selectedRequest.requestingEmployee?.primaryRole})</span>
                </p>
              </div>

              {selectedRequest.targetEmployee && (
                <div>
                  <p className="text-sm text-slate-500">Covering Employee</p>
                  <p className="font-medium">
                    {selectedRequest.targetEmployee?.firstName} {selectedRequest.targetEmployee?.lastName}
                    <span className="text-slate-500 ml-2">({selectedRequest.targetEmployee?.primaryRole})</span>
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-slate-500">Shift Details</p>
                {selectedRequest.originalAssignment?.shiftInstance && (
                  <div className="p-3 bg-slate-50 rounded-lg mt-1">
                    <p className="font-medium text-slate-900">
                      {selectedRequest.originalAssignment.shiftInstance.name}
                    </p>
                    <p className="text-sm text-slate-600">
                      {format(parseISO(selectedRequest.originalAssignment.shiftInstance.date), 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-sm text-slate-600">
                      {formatShiftTime(
                        selectedRequest.originalAssignment.scheduledStart,
                        selectedRequest.originalAssignment.scheduledEnd
                      )}
                    </p>
                    <p className="text-sm text-slate-500 mt-1 capitalize">
                      {selectedRequest.originalAssignment.role}
                    </p>
                  </div>
                )}
              </div>

              {selectedRequest.reason && (
                <div>
                  <p className="text-sm text-slate-500">Reason</p>
                  <p className="text-slate-700">{selectedRequest.reason}</p>
                </div>
              )}

              <div>
                <label className="text-sm text-slate-500 block mb-1">Notes (optional)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="input"
                  rows={2}
                  placeholder="Add a note..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setReviewNotes('');
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => denyMutation.mutate(selectedRequest.id)}
                disabled={denyMutation.isPending}
                className="flex-1 btn btn-danger flex items-center justify-center gap-2"
              >
                {denyMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Deny
              </button>
              <button
                onClick={() => approveMutation.mutate(selectedRequest.id)}
                disabled={approveMutation.isPending || !selectedRequest.targetEmployee}
                className="flex-1 btn btn-primary flex items-center justify-center gap-2"
              >
                {approveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Approve
              </button>
            </div>

            {!selectedRequest.targetEmployee && (
              <p className="text-xs text-warning-600 mt-3 text-center">
                Cannot approve until someone accepts the swap
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
